import { useEffect, useState } from 'react'
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme
} from '@mui/material'
import { LegacyWrapHeader } from '../../_types/daily-wrap.type'

interface LegacyWrapNavProps {
  headers: LegacyWrapHeader[]
  // bumps when wrap content reloads so we re-bind the IntersectionObserver
  // against fresh anchor elements
  contentVersion: number
}

const SCROLL_OFFSET = 80

export default function LegacyWrapNav ({
  headers,
  contentVersion
}: LegacyWrapNavProps) {
  const theme = useTheme()
  const [activeId, setActiveId] = useState<string | null>(null)

  const handleClick = (anchorId: string) => {
    const el = document.getElementById(anchorId)
    if (!el) return
    const top = el.getBoundingClientRect().top + window.pageYOffset - SCROLL_OFFSET
    window.scrollTo({ top, behavior: 'smooth' })
    setActiveId(anchorId)
  }

  useEffect(() => {
    if (!headers.length) return

    const elements = headers
      .map(h => document.getElementById(h.anchorId))
      .filter((el): el is HTMLElement => !!el)

    if (!elements.length) return

    const observer = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .map(e => e.target.id)
        if (!visible.length) return
        // Pick the first header (in document order) that's in the band.
        for (const h of headers) {
          if (visible.includes(h.anchorId)) {
            setActiveId(h.anchorId)
            return
          }
        }
      },
      {
        // Narrow band near the top of the viewport: anchors are "active"
        // when their top crosses SCROLL_OFFSET from the top.
        rootMargin: `-${SCROLL_OFFSET}px 0px -70% 0px`,
        threshold: 0
      }
    )

    elements.forEach(el => observer.observe(el))

    setActiveId(prev => prev ?? headers[0]?.anchorId ?? null)

    return () => observer.disconnect()
  }, [headers, contentVersion])

  if (!headers.length) return null

  return (
    <Box>
      <List dense disablePadding>
        {headers.map(h => (
          <ListItem key={h.anchorId} disablePadding>
            <ListItemButton
              onClick={() => handleClick(h.anchorId)}
              selected={activeId === h.anchorId}
              sx={{
                borderLeft: '3px solid',
                borderLeftColor:
                  activeId === h.anchorId
                    ? theme.palette.primary.main
                    : 'transparent',
                pl: 1.5
              }}
            >
              <ListItemText
                primary={h.title}
                primaryTypographyProps={{
                  fontWeight: activeId === h.anchorId ? 'bold' : 'regular',
                  color:
                    activeId === h.anchorId ? 'primary.main' : 'text.primary',
                  variant: 'body2'
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  )
}
